import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useStore } from 'react-redux';
import { useCurrentPSAP } from '@rsos/reduxDataStore/src/features/capstone/psapInfo';
import { ALIService } from '../services/ali';
import { sendLocation } from '../services/els';
import { Location } from '../types';

const DEGREES_IN_METES_APROX = 0.00001;
const STEP_SIZE = 10;

interface UsePlotLocationsProps {
  phoneNumber: string;
  includeSecondaryLocation: boolean;
  sendSecondaryLocationFirst: boolean;
  location?: Location;
  classOfService?: string;
  secondaryCallType?: string;
}

const usePlotLocations = ({
  phoneNumber,
  includeSecondaryLocation,
  sendSecondaryLocationFirst,
  location,
  classOfService,
  secondaryCallType,
}: UsePlotLocationsProps) => {
  const [currentLocation, setCurrentLocation] = useState(location);
  const [nextLocation, setNextLocation] = useState(location);
  const store = useStore();
  const aliService = useMemo(() => new ALIService(store), [store]);
  const psap = useCurrentPSAP();
  const plottingInterval = useRef<NodeJS.Timeout | undefined>(undefined);
  const locationsSent = useRef(new Map<string, number>());
  const [isPlotting, setIsPlotting] = useState(false);

  /**
   * Plot next location and update current coordinates for next call
   */
  const plotNextLocation = useCallback(() => {
    if (!nextLocation) {
      throw new Error('Initial latitude / longitude not set.');
    }

    const numLocations = locationsSent.current.get(phoneNumber) || 0;

    const isFirstLocAndSendSecondaryFirst =
      sendSecondaryLocationFirst && numLocations === 0;
    const isSecondLocAndNotSendSecondaryFirst =
      !sendSecondaryLocationFirst && numLocations === 1;
    const shouldSendSecondaryLocation =
      includeSecondaryLocation &&
      (isFirstLocAndSendSecondaryFirst || isSecondLocAndNotSendSecondaryFirst);

    if (shouldSendSecondaryLocation) {
      aliService.sendSecondaryLocation({
        latitude: nextLocation.latitude,
        longitude: nextLocation.longitude,
        agencyId: psap.agency_id,
        phoneNumber,
        classOfService,
        secondaryCallType,
      });
    } else {
      sendLocation({
        latitude: nextLocation.latitude,
        longitude: nextLocation.longitude,
        phoneNumber,
      });
    }

    setCurrentLocation(nextLocation);
    setNextLocation({
      ...nextLocation,
      latitude: nextLocation.latitude + DEGREES_IN_METES_APROX * STEP_SIZE,
    });
    locationsSent.current.set(phoneNumber, numLocations + 1);
  }, [
    nextLocation,
    includeSecondaryLocation,
    sendSecondaryLocationFirst,
    phoneNumber,
    psap.agency_id,
    aliService,
    classOfService,
    secondaryCallType,
  ]);

  /**
   * Update the locations when the input changes
   */
  useEffect(() => {
    setNextLocation(location);

    if (!currentLocation) {
      setCurrentLocation(location);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  /**
   * Handle re-registering callback for continuous plotting
   */
  useEffect(() => {
    if (!isPlotting) return;

    plottingInterval.current = setInterval(plotNextLocation, 2000);

    return () => {
      if (plottingInterval.current) {
        clearInterval(plottingInterval.current);
        plottingInterval.current = undefined;
      }
    };
  }, [isPlotting, plotNextLocation]);

  /**
   * Expose function to start continuous plotting
   */
  const startPlotting = () => setIsPlotting(true);

  /**
   * Expose function to puase continous plotting
   */
  const pausePlotting = () => setIsPlotting(false);

  return {
    startPlotting,
    pausePlotting,
    plotNextLocation,
    currentLocation,
    isPlotting,
  };
};

export default usePlotLocations;
